import React, {useState} from 'react'
import {useIntl, FormattedMessage} from 'react-intl'
import {useQuery} from 'react-query'
import {getOneById} from '../../../../services/common_requests'
import {useListView} from '../core/ListViewProvider'
import {Modal} from 'react-bootstrap'
import {KTSVG} from '../../../../../_dashboard/helpers'
import {User} from '../core/_models'
import {UserConnectionInsights} from '../components/UserConnectionInsights'

const formatGender = (gender: string) => {
  if (!gender) return ''
  return <FormattedMessage id={`USERS.GENDER.${gender.toUpperCase()}`} />
}

const formatRole = (role: User['role'] | undefined) => {
  if (!role) return 'N/A'
  if (typeof role === 'object' && role.name) return role.name
  return String(role)
}

const getColorFromString = (str: string) => {
  const colors = ['primary', 'success', 'info', 'warning', 'danger', 'dark', 'light']
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }
  const index = Math.abs(hash) % colors.length
  return colors[index]
}

const getInitials = (first?: string, last?: string) => {
  const a = (first || '').slice(0, 1)
  const b = (last || '').slice(0, 1)
  return `${a}${b}`.toUpperCase() || '??'
}

const UserDetailsModal: React.FC = () => {
  const intl = useIntl()
  const {viewUserId, setViewUserId} = useListView()
  const [user, setUser] = useState<User | null>(null)

  const {isLoading} = useQuery(
    ['user', viewUserId],
    () => getOneById(viewUserId as string, `/users`),
    {
      enabled: !!viewUserId,
      onSuccess: (data) => {
        setUser(data)
      },
    },
  )

  const handleClose = () => {
    setViewUserId(null)
  }

  const userAny = user as User & {
    lastName?: string
    numConnections?: number
    connectionSummary?: Record<string, unknown>
    connectionHistory?: Array<{at?: string; ip?: string; userAgent?: string}>
  }

  return (
    <Modal show={!!viewUserId} onHide={handleClose} size='lg'>
      <Modal.Header closeButton>
        <Modal.Title>{intl.formatMessage({id: 'USERS.DETAILS.TITLE'})}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading ? (
          <div className='py-5 text-center text-gray-600'>
            {intl.formatMessage({id: 'USERS.DETAILS.LOADING'})}
          </div>
        ) : (
          <div className='d-flex flex-column gap-5'>
            <div className='d-flex align-items-center'>
              <div className='symbol symbol-50px me-5'>
                {user?.avatar ? (
                  <img src={user.avatar} alt='' className='rounded-circle' />
                ) : (
                  <div
                    className={`symbol-label bg-light-${getColorFromString(user?.firstName || '')} text-${getColorFromString(user?.firstName || '')} fw-bold fs-4`}
                  >
                    {getInitials(user?.firstName, userAny?.lastName)}
                  </div>
                )}
              </div>
              <div className='d-flex flex-column flex-grow-1'>
                <span className='text-gray-800 fs-4 fw-bold'>
                  {[user?.firstName, userAny?.lastName].filter(Boolean).join(' ')}
                </span>
                <span className='text-gray-500 fw-semibold'>{user?.email}</span>
              </div>
              <span
                className={`badge fw-semibold ${user?.status?.toUpperCase() === 'ACTIVE' ? 'bg-success' : 'bg-danger'}`}
                style={{color: '#fff'}}
              >
                {intl.formatMessage({id: `USERS.STATUS.${user?.status?.toUpperCase()}`})}
              </span>
            </div>

            <UserConnectionInsights
              status={userAny?.status}
              lastConnection={userAny?.lastConnection}
              numConnections={userAny?.numConnections}
              connectionSummary={userAny?.connectionSummary as any}
              connectionHistory={userAny?.connectionHistory}
            />

            <div>
              <span className='text-gray-600 fs-7 fw-semibold text-uppercase mb-4 d-block'>
                {intl.formatMessage({id: 'USERS.DETAILS.INFORMATION', defaultMessage: 'Informations'})}
              </span>
              <div className='row g-5'>
                <div className='col-md-6'>
                  <div className='d-flex flex-column'>
                    <span className='text-gray-800 fs-6 fw-bold d-flex align-items-center mb-2'>
                      <KTSVG path='/media/icons/duotune/general/gen040.svg' className='svg-icon-1 me-2 text-warning' />
                      {intl.formatMessage({id: 'USERS.DETAILS.ROLE'})}
                    </span>
                    <span className='fw-semibold ps-10 text-gray-800'>{formatRole(user?.role)}</span>
                  </div>
                </div>
                <div className='col-md-6'>
                  <div className='d-flex flex-column'>
                    <span className='text-gray-800 fs-6 fw-bold d-flex align-items-center mb-2'>
                      <KTSVG path='/media/icons/duotune/communication/com006.svg' className='svg-icon-1 me-2 text-primary' />
                      {intl.formatMessage({id: 'USERS.DETAILS.GENDER'})}
                    </span>
                    <span className='fw-semibold ps-10 text-gray-800'>{formatGender(user?.gender || '')}</span>
                  </div>
                </div>
                <div className='col-md-6'>
                  <div className='d-flex flex-column'>
                    <span className='text-gray-800 fs-6 fw-bold d-flex align-items-center mb-2'>
                      <KTSVG path='/media/icons/duotune/general/gen026.svg' className='svg-icon-1 me-2 text-info' />
                      {intl.formatMessage({id: 'USER.PREFERRED_LANGUAGE'})}
                    </span>
                    <span className='fw-semibold ps-10 text-gray-800'>{user?.preferredLanguage || 'N/A'}</span>
                  </div>
                </div>
                <div className='col-md-6'>
                  <div className='d-flex flex-column'>
                    <span className='text-gray-800 fs-6 fw-bold d-flex align-items-center mb-2'>
                      <KTSVG path='/media/icons/duotune/general/gen049.svg' className='svg-icon-1 me-2 text-success' />
                      {intl.formatMessage({id: 'USER.ID_TYPE'})}
                    </span>
                    <span className='fw-semibold ps-10 text-gray-800'>{user?.typeOfID || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type='button' className='btn btn-light' onClick={handleClose}>
          {intl.formatMessage({id: 'ACTION.CLOSE'})}
        </button>
      </Modal.Footer>
    </Modal>
  )
}

export {UserDetailsModal}
